import { FC, useContext, useEffect, useState } from "react";

import { useMutation } from "react-query";
import client from "../../../api";
import { getErrorMessage, logger } from "../../../lib/utils";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import {
  NetworkDisconnectMsg,
  APISomethingWrongMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";
import { Tab } from "@headlessui/react";
import classNames from "classnames";
import { SpinnerIcon } from "src/components/icons/spinner-icon";
import BusinessGeneralLimitationsPage from "../billings/business-billings";
import BusinessDailyLimitationsPage from "../billings/business-daily-billings";
import GamblingBusinessDailyLimitations from "./daily-business";

interface BusinessGamblingLimitationsMgtProps {
  permissionModule?: string | string[];
}

const BusinessGamblingLimitationsMgt: FC<BusinessGamblingLimitationsMgtProps> = ({
  permissionModule = permissionConstants.LIMITATIONS_MONEY_MOVERS_BUSINESS,
}) => {
  const { canView, canList, canExport } = usePermissions(permissionModule);
  const canViewLimitations = canView || canList;

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [activeTab, setActiveTab] = useState<boolean>(true);

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }

  const tabLists = ["Single", "Daily"];

  const makeTabList = () => {
    const tabListsUI = tabLists.map((item, index) => (
      <Tab
        key={`tabs_${index}`}
        className={({ selected }) => {
          setActiveTab(selected);
          return classNames(
            "h-full font-medium text-[14px] md:text-[16px] relative px-2 py-2 md:px-8 md:py-4 before:absolute before:left-0 before:bottom-0 before:h-0.5 before:bg-brand before:transition-all before:duration-300 before:ease-in-out",
            "ring-transparent ring-opacity-60 ring-offset-0 ring-offset-transparent focus:outline-none focus:ring-0",
            selected
              ? "text-primary bg-white rounded-t-[8px]"
              : "text-secondary bg-transparent"
          );
        }}
      >
        {item}
      </Tab>
    ));
    return (
      <Tab.List className="flex flex-row items-center w-full h-[58px] md:h-[56px]">
        {tabListsUI}
      </Tab.List>
    );
  };

  const { mutate: exportLimitations, isLoading: exportListLoading } =
    useMutation(
      activeTab ? client.dailyLimits.export : client.limitationsBusiness.export,
      {
        onSuccess: (data) => {
          logger.log(data);
          setAlertText("Export success");
          setIsAlertOpened(true);
        },
        onError: (error: any) => {
          if (error.code === "ERR_NETWORK") {
            setAlertText(NetworkDisconnectMsg);
          } else {
            if (error.response) {
              setAlertText(getErrorMessage(error.response.data));
            } else {
              setAlertText(APISomethingWrongMsg);
            }
          }
          setIsAlertOpened(true);
        },
      }
    );

  const exportBtn = async () => {
    if (!canExport) {
      setAlertText("You do not have permission to export limitations");
      setIsAlertOpened(true);
      return;
    }
    try {
      setTimeout(() => {
        exportLimitations();
      }, 300);
    } catch (errInfo) {
      logger.log("Validate Failed:", errInfo);
    }
  };

  if (!canViewLimitations) {
    return (
      <div className="w-full h-full bg-gray p-4">
        <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view limitations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray">
      <div className="w-full h-full flex-col p-2 sm:p-4">
        <div className="title-block flex mb-16">
          <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px] mb-4">
            Limitations
          </p>
          <div className="title-actions">
            <PermissionGate
              module={permissionModule}
              action="can_export"
            >
              <button
                type="button"
                onClick={() => exportBtn()}
                disabled={exportListLoading}
                className="button button--type3 button--gap30 min-w-[160px]"
                style={{ minWidth: 160 }}
              >
                {exportListLoading ? (
                  <div className="flex justify-center items-center w-full">
                    <SpinnerIcon className="h-auto w-5 animate-spin" />
                  </div>
                ) : (
                  <>
                    Export Excel
                    <span className="btn-icon btn-icon--fill">
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 17 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.79961 1.8002L12.6996 5.4002M8.79961 1.8002L5.19961 5.4002M8.79961 1.8002L8.79961 13.2002M15.9996 8.4002V16.2002L1.59961 16.2002L1.59961 8.4002"
                          stroke="#232800"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        ></path>
                      </svg>
                    </span>
                  </>
                )}
              </button>
            </PermissionGate>
          </div>
        </div>
        <Tab.Group>
          {makeTabList()}
          <Tab.Panels>
            <Tab.Panel className="border-0 focus:border-0 focus:ring-0 ring-0">
              <BusinessGeneralLimitationsPage permissionModule={permissionModule} />
            </Tab.Panel>
            <Tab.Panel className="border-0 focus:border-0 focus:ring-0 ring-0">
              <GamblingBusinessDailyLimitations permissionModule={permissionModule} />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );
};

export default BusinessGamblingLimitationsMgt;
